import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DistaMarkdownViewer } from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import {
  LoadingGuard,
  Organization,
  SistaId,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import {
  OrganizationChangeNoticeDTO,
  OrganizationChangeNoticeStatus,
} from "@sista/library-stasi/dist/api/utils/buildRejstrikClient";
import { SistaFistoSingle, SistaFistoView } from "@sista/service-fisto-ui";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CHANGE_NOTICE_STATUS_CONFIG } from "./OrganizationChangeNoticeDetailContent";
import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";
import { OrganizationChangeNoticeDialog } from "../../dialogs/organization/OrganizationChangeNoticeDialog";

export type OrganizationChangeNoticesContentProps = {
  organization: Organization;
};

const STATUS_CONFIG = CHANGE_NOTICE_STATUS_CONFIG;

const ExpandedRow = (props: {
  organization: Organization;
  notice: OrganizationChangeNoticeDTO;
  colSpan: number;
}) => {
  const { t } = useTranslation("ucet");
  const { loggedAccount } = useContext(IdmClientContext);

  const organizationUrn = new SistaId(
    "organizations",
    props.organization.id,
  ).asUrn();
  const changeNoticeUrn = new SistaId(
    "organizationChangeNotice",
    props.notice.id,
  ).asUrn();
  const fistoContext = [organizationUrn, changeNoticeUrn];

  return (
    <TableRow>
      <TableCell colSpan={props.colSpan} sx={{ p: 0, border: 0 }}>
        <Collapse in unmountOnExit>
          <Box sx={{ p: 2 }}>
            {props.notice.description && (
              <Box sx={{ mb: 2 }}>
                <DistaMarkdownViewer value={props.notice.description} />
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                  {t("ucet.organizationChangeNotice.attachments", "Přílohy")}
                </Typography>
                <SistaFistoView
                  authKey={loggedAccount.authKey}
                  category="organizationChangeMeta"
                  context={fistoContext}
                  deleteDisabled={true}
                  uploadDisabled={true}
                />
              </Grid>
              {props.notice.confirmationFileId && (
                <Grid item xs={6}>
                  <Typography
                    variant="body1"
                    fontWeight={"bold"}
                    sx={{ mb: 1 }}
                  >
                    {t(
                      "ucet.organizationChangeNotice.confirmationFile",
                      "Potvrzení oznámení",
                    )}
                  </Typography>
                  <SistaFistoSingle
                    authKey={loggedAccount.authKey}
                    fileId={props.notice.confirmationFileId}
                    deleteDisabled={true}
                    variant="link"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export const OrganizationChangeNoticesContent = (
  props: OrganizationChangeNoticesContentProps,
) => {
  const { t } = useTranslation("ucet");
  const rej = useRejstrikClient();
  const navigate = useNavigate();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [listCall, listState] = useSafeSistaAsync(
    rej.getOrganizationListChangeNotices,
  );

  const reload = useCallback(() => {
    listCall({ organizationId: props.organization.id });
  }, [listCall, props.organization.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const notices = useMemo(() => {
    if (listState.value == null) {
      return null;
    }
    return [...listState.value].sort(
      (a, b) =>
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    );
  }, [listState.value]);

  return (
    <Paper variant="infoCard" sx={{ mt: 2, width: "100%" }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={() => setShowCreateDialog(true)}>
          {t("ucet.organizationChangeNotice.newNotice", "Oznámit změnu")}
        </Button>
      </Stack>

      <LoadingGuard state={listState}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180 }}>
                  {t("ucet.organizationChangeNotice.table.id", "ID")}
                </TableCell>
                <TableCell>
                  {t("ucet.organizationChangeNotice.table.subject", "Změna")}
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {t(
                    "ucet.organizationChangeNotice.table.createdDate",
                    "Datum oznámení",
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {t(
                    "ucet.organizationChangeNotice.table.settlementDate",
                    "Datum vyřízení",
                  )}
                </TableCell>
                <TableCell>
                  {t("ucet.organizationChangeNotice.table.status", "Stav")}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {notices == null ||
                (notices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "ucet.organizationChangeNotice.empty",
                          "Žádná oznámení změn",
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              {notices != null &&
                notices.map((notice) => {
                  const status = STATUS_CONFIG[notice.status] ?? {
                    label: notice.status,
                    defaultValue: notice.status,
                    backgroundColor: "#dadde1",
                  };
                  const isExpanded = expandedId === notice.id;
                  return (
                    <>
                      <TableRow
                        key={notice.id}
                        hover
                        selected={isExpanded}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          setExpandedId(isExpanded ? null : notice.id)
                        }
                      >
                        <TableCell
                          sx={{ fontFamily: "monospace", fontSize: "0.8em" }}
                        >
                          {notice.id}
                        </TableCell>
                        <TableCell>{notice.subject}</TableCell>
                        <TableCell>
                          {notice.createdDate
                            ? new Date(notice.createdDate).toLocaleDateString(
                                "cs-CZ",
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {notice.settlementDate
                            ? new Date(
                                notice.settlementDate,
                              ).toLocaleDateString("cs-CZ")
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={String(t(status.label, status.defaultValue))}
                            size="small"
                            sx={{ backgroundColor: status.backgroundColor }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip
                            title={t(
                              "ucet.organizationChangeNotice.table.detail",
                              "Detail oznámení",
                            )}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/sprava-organizace/${props.organization.id}/change-notice/${notice.id}`,
                                )
                              }
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <ExpandedRow
                          organization={props.organization}
                          notice={notice}
                          colSpan={6}
                        />
                      )}
                    </>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </LoadingGuard>

      {showCreateDialog && (
        <OrganizationChangeNoticeDialog
          organization={props.organization}
          onDone={() => {
            setShowCreateDialog(false);
            reload();
          }}
          onCancel={() => setShowCreateDialog(false)}
        />
      )}
    </Paper>
  );
};
